import { NgStyle } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { getContrastColorSafe } from '@shared-angular';
import { LoginService } from '@shared-angular/services/login.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { blockedStatesArr, ChatbotConfig, HistoryItemResponse } from '@shared/index';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BotChatDialogComponent } from 'src/app/shared/components/ai-bot/utils/bot-chat-dialog/bot-chat-dialog.component';
import { BotPromptComponent } from 'src/app/shared/components/ai-bot/utils/bot-prompt/bot-prompt.component';
import { UserDocumentsComponent } from 'src/app/shared/components/ai-bot/utils/user-documents/user-documents.component';
import { DisconnectOverlayComponent } from 'src/app/shared/components/disconnect-overlay/disconnect-overlay.component';
import { MicPermissionOverlayComponent } from 'src/app/shared/components/mic-permission-overlay/mic-permission-overlay.component';
import { BotMessagesService } from 'src/app/shared/services/bot-messages.service';
import { BotService } from 'src/app/shared/services/bot.service';
import { IframeCommunicationService } from 'src/app/shared/services/iframe-communication/iframe-communication.service';
import { OverlayManagerService } from 'src/app/shared/services/overlay-manager.service';
import { PlayAudioService } from 'src/app/shared/services/playaudio.service';
import { SpeechService } from 'src/app/shared/services/speech.service';
import { SocketService } from '../../../shared/services/socket.service';
import { StatisticService } from '../admin/tabs/insights/statistics-tab/statistic.service';
import { BotBasedComponent } from '../bot-based/bot-based.component';


@Component({
  templateUrl: './bot.component.html',
  styleUrls: ['./bot.component.scss'],
  imports: [NgStyle, BotChatDialogComponent,
    FormsModule,
    BotPromptComponent,
    BsDropdownModule,
    DisconnectOverlayComponent,
    MicPermissionOverlayComponent,
    UserDocumentsComponent
  ],
  providers: [BsModalService]
})
export class BotComponent extends BotBasedComponent implements OnInit, OnDestroy {
  protected getContrastColor = getContrastColorSafe;

  constructor(
    public override loginService: LoginService,
    public override speechService: SpeechService,
    public override statisticsService: StatisticService,
    public override iframeCommunication: IframeCommunicationService,
    public override playService: PlayAudioService,
    public override route: ActivatedRoute,
    public override toastr: ToastrService,
    public override bot: BotService,
    public override socketService: SocketService,
    public override modalService: BsModalService,
    public override domSanatizer: DomSanitizer,
    protected override botMessages: BotMessagesService,
    public override logger: RagLoggerService,
    protected override overlayManager: OverlayManagerService
  ) {
    super(route, loginService, speechService, statisticsService, iframeCommunication, playService, bot, socketService, toastr, modalService, domSanatizer, botMessages, logger, overlayManager);
    route.data.subscribe(data => {
      const botConfig = data['botConfig'] as ChatbotConfig | null;
      if (!botConfig) return;
      this.botConfig = botConfig;
      this.updateBrowserTitle();
      void this.removePassThrough();
    });

    this.route.queryParams.subscribe(params => {
      this.recognitionLanguage = "" + (params['recognitionlanguage'] ?? 'de-DE');
    });

    this.subscriptions.push(
      this.socketService.onMessageDeletion.subscribe(data => {
        const delOpenAiMsgIds = data.messages.map((msg: HistoryItemResponse) => msg._id);
        this.botMessages.deleteMessages(delOpenAiMsgIds);
      }),
      this.socketService.onCreateMessagesInThread.subscribe(() => {
        void this.bot.scrollDown();
      }),
      this.socketService.onAddUserMessage.subscribe(() => {
        this.isSending = false;
        void this.bot.scrollDown();
      }),
      this.socketService.onLogoutOldSession.subscribe((data) => {
        if (this.loginService.loggedInUser && this.loginService.loggedInUser._id === data.oldSessionId) {
          void this.loginService.logout(false);
        }
      })
    );
  }

  public override onRunUpdate(data: Omit<HistoryItemResponse, 'content'> & { snapshot: string; deltaStr: string; }) {
    super.onRunUpdate(data);
    void this.bot.scrollDown();
  }


  public override async ngOnInit() {
    /*** MUSS LEIDER GLOBAL IRGENDWO HIN ***/
    document.body.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest('.docsearch-link');
      if (target) {
        e.preventDefault();
        const url = target.getAttribute('href');
        if (url) {
          console.log("Intercepted click. URL:", url);
          window.RAGAI.DOCSEARCH.clickFn(url); // ← deine Funktion
          //RAGAI.CARDS.iframe(url); // ← deine Funktion
        }
      }
    });

    this.speechService.textShouldBeSend.subscribe((send: boolean) => {
      if (send) {
        void this.add();
        this.promptInput = '';
        this.playService.stopAudio();
      }
    });

    this.playService.currentAudioStatus.subscribe((status: [string, string]) => {
      if (status[0] === 'idle') {
        this.promptInput = '';
      }
    });
    this.speechService.recognisedTextChanged.subscribe((text: string) => {
      if (this.isThinking || this.playService.anyAudioIsLoading() || this.playService.anyAudioIsPlaying()) return;
      this.promptInput = text;
    });

    this.iframeCommunication.msgManager.runRequestEmitter.subscribe((prompt: string) => {
      void this.add(prompt, true);
    });

    this.iframeCommunication.msgManager.userMessageEmitter.subscribe((message: string) => {
      this.promptInput = message;
      void this.add();
    });

    if (this.botConfig as ChatbotConfig | null) {
      this.customCssUrl = this.getCustomCssUrl(this.botConfig._id, "chat");
      if (this.loginService.loggedInUser === null) {
        // persist=false: Token wird erst nach Bestaetigung des Datenschutzhinweises persistiert
        await this.loginService.loadMyUser(false);
      }
      await this.showGDPR();
      this.usertokenService.persistToken();

      this.injectJs(this.botConfig._id);
      if (this.botConfig.voiceInputChatActive || this.botConfig.voiceOutputChatActive) {
        this.speechService.init(this.recognitionLanguage);
      }
      this.socketService.connectToThread(this.botConfig._id);
    }
  }

  @HostListener('window:click', ['$event'])
  public async autoMessage(event: Event) {
    if ((event.target as HTMLElement).className.includes('message-button')) {
      this.promptInput = (event.target as HTMLElement).innerText;
      await this.add();
    }
  }

  //nonPomtInput wenn über BotApi Prompt gesendet wird dann das Input feld in ruhe lassen
  public override async add(nonPromtInput: string | null = null, bypass: boolean = false) {
    this.isSending = true;
    void this.bot.scrollDown();
    //    this.logger.callerInfo(this);

    let pInput = nonPromtInput ?? this.promptInput;
    this.logger.measureRunStart(pInput);


    if (pInput === '') return;
    //? Check for stop words
    const foundStopWords = this.promptContainsStopWord(pInput, this.botConfig.stopWords);
    if (foundStopWords.length > 0) {
      this.toastr.error("SENT", "Unerwünschte Wörter gefunden: " + foundStopWords.join(", "));
      this.isSending = false;
      return;
    }
    //? Check for word replacements
    pInput = this.runWordReplacer(pInput);
    this.botState = 'thinking';
    this.isThinking = true;

    this.speechService.botIsThinkking = true;
    if (nonPromtInput == null) {
      this.promptInput = "";
    }
    window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.MSG.prompt", { pInput });


    const res = await this.bot.chat(this.botConfig._id, pInput, this.botMessages.getMessages(), bypass);
    if (res) {
      window.RAGAI.lastThreadId = res.threadId;
    } else {
      this.isSending = false;
      this.isThinking = false;
      this.botState = 'idle';
    }

    void this.bot.scrollDown();
  }

  public getColorFromConfig(background: boolean = false): string {
    return this.bot.getColorFromConfig(this.botConfig, background);
  }



  protected thinkingAnimaitionShouldBeShown(): boolean {
    if (this.prevBotState === 'unknown') return false;
    if (this.isRecivingDeltas) return false;
    const checkStates: string[] = [...blockedStatesArr];
    return checkStates.includes(this.botState);
  }
}
